<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
/* global axios */

const emit = defineEmits(['close', 'connected']);

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);
const branches = computed(() => store.getters['branches/getBranches'] || []);

// Estados: 'form' → 'connecting' → 'qr' → 'connected' | 'error'
const step = ref('form');

const form = ref({ name: '', branch_id: null });
const errorMsg = ref('');
const isCreating = ref(false);

const inboxId = ref(null);
const statusUrl = ref(null);
const qrDataUrl = ref(null);
const connectedInfo = ref(null);
let pollingTimer = null;

onMounted(() => {
  store.dispatch('branches/get');
});

onBeforeUnmount(() => {
  if (pollingTimer) clearInterval(pollingTimer);
});

async function startConnect() {
  if (!form.value.name.trim()) {
    errorMsg.value = 'El nombre es obligatorio';
    return;
  }
  if (!form.value.branch_id) {
    errorMsg.value = 'Elige una sucursal';
    return;
  }
  isCreating.value = true;
  errorMsg.value = '';
  try {
    const { data } = await axios.post(
      `/api/v1/accounts/${accountId.value}/whatsapp_sessions`,
      { name: form.value.name.trim(), branch_id: form.value.branch_id }
    );
    inboxId.value = data.inbox_id;
    statusUrl.value = data.bridge_status_url;
    step.value = 'connecting';
    startPolling();
  } catch (err) {
    errorMsg.value = err?.response?.data?.error || err?.message || 'Error al crear el inbox';
  } finally {
    isCreating.value = false;
  }
}

async function pollStatus() {
  if (!statusUrl.value) return;
  try {
    const { data } = await axios.get(statusUrl.value);
    if (data?.connected) {
      connectedInfo.value = data.me;
      step.value = 'connected';
      qrDataUrl.value = null;
      if (pollingTimer) {
        clearInterval(pollingTimer);
        pollingTimer = null;
      }
      // Refresca lista de inboxes en el store para que aparezca el nuevo
      try { await store.dispatch('inboxes/get'); } catch (_) { /* noop */ }
      return;
    }
    if (data?.qrDataUrl) {
      qrDataUrl.value = data.qrDataUrl;
      step.value = 'qr';
    } else {
      step.value = 'connecting';
    }
  } catch (err) {
    /* el bridge puede tardar 1-2s en levantar la sesión, ignoramos errores transitorios */
  }
}

function startPolling() {
  pollStatus();
  pollingTimer = setInterval(pollStatus, 2000);
}

function close() {
  if (pollingTimer) clearInterval(pollingTimer);
  emit('close');
}

function finish() {
  if (pollingTimer) clearInterval(pollingTimer);
  emit('connected', { inbox_id: inboxId.value });
  emit('close');
}
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center"
    style="background: rgba(0,0,0,0.7); backdrop-filter: blur(6px);"
    @click.self="close"
  >
    <div
      class="w-full max-w-md rounded-2xl overflow-hidden border"
      style="background: #0a0a0a; border-color: rgba(251,191,36,0.4); box-shadow: 0 20px 60px rgba(251,191,36,0.15), 0 20px 60px rgba(0,0,0,0.7);"
    >
      <!-- Header -->
      <div class="px-5 py-4 border-b flex items-center justify-between" style="border-color: rgba(255,255,255,0.08);">
        <div class="flex items-center gap-2.5">
          <span class="text-2xl">💬</span>
          <h2 class="text-base font-bold text-white">Conectar WhatsApp</h2>
        </div>
        <button
          type="button"
          class="text-zinc-500 hover:text-white text-2xl leading-none"
          @click="close"
        >×</button>
      </div>

      <!-- Body -->
      <div class="p-5">
        <!-- Step 1 · Form -->
        <div v-if="step === 'form'" class="space-y-4">
          <div>
            <label class="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style="color: #fbbf24;">
              Nombre del inbox
            </label>
            <input
              v-model="form.name"
              type="text"
              placeholder="WhatsApp La Romana"
              class="w-full px-3 py-2 rounded-lg text-sm text-white outline-none border focus:border-yellow-500"
              style="background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.12);"
              @keyup.enter="startConnect"
            />
          </div>

          <div>
            <label class="block text-[11px] font-bold uppercase tracking-wider mb-1.5" style="color: #fbbf24;">
              Sucursal
            </label>
            <select
              v-model="form.branch_id"
              class="w-full px-3 py-2 rounded-lg text-sm text-white outline-none border focus:border-yellow-500"
              style="background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.12);"
            >
              <option :value="null" disabled>Elige una sucursal…</option>
              <option v-for="b in branches" :key="b.id" :value="b.id" class="bg-zinc-900">
                {{ b.code }} · {{ b.name }}
              </option>
            </select>
            <p class="text-[10px] text-zinc-500 mt-1">
              Las conversaciones nuevas que entren a este número se asignarán automáticamente a la sucursal elegida.
            </p>
          </div>

          <p v-if="errorMsg" class="text-sm text-rose-400">{{ errorMsg }}</p>

          <button
            type="button"
            class="w-full py-3 rounded-lg text-sm font-bold text-black transition disabled:opacity-50"
            style="background: linear-gradient(135deg,#fbbf24,#d97706); box-shadow: 0 0 16px rgba(251,191,36,0.4);"
            :disabled="isCreating"
            @click="startConnect"
          >
            {{ isCreating ? 'Creando…' : 'Conectar' }}
          </button>
        </div>

        <!-- Step 2 · Connecting (waiting for QR) -->
        <div v-else-if="step === 'connecting'" class="text-center py-10 space-y-3">
          <div class="inline-block w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-sm text-zinc-300">Iniciando sesión WhatsApp…</p>
        </div>

        <!-- Step 3 · QR -->
        <div v-else-if="step === 'qr'" class="text-center space-y-3">
          <div class="rounded-lg p-4 inline-block" style="background: white;">
            <img :src="qrDataUrl" alt="QR WhatsApp" class="w-[260px] h-[260px]" />
          </div>
          <div class="text-left text-[12px] text-zinc-300 space-y-1.5">
            <p class="font-bold text-white">Escanea el QR con WhatsApp:</p>
            <p>1. Abre WhatsApp en tu celular</p>
            <p>2. Toca <strong class="text-yellow-400">⋮ → Dispositivos vinculados</strong></p>
            <p>3. Toca <strong class="text-yellow-400">"Vincular un dispositivo"</strong></p>
            <p>4. Apunta la cámara al código QR</p>
          </div>
          <p class="text-[10px] text-zinc-500 italic">El QR se actualiza cada 30s. Si caduca, se regenerará automáticamente.</p>
        </div>

        <!-- Step 4 · Connected -->
        <div v-else-if="step === 'connected'" class="text-center py-8 space-y-3">
          <div
            class="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-4xl"
            style="background: rgba(34,197,94,0.15); border: 2px solid #22c55e;"
          >✓</div>
          <h3 class="text-lg font-bold text-white">¡WhatsApp conectado!</h3>
          <p class="text-sm text-zinc-400" v-if="connectedInfo">
            Número: <span class="text-yellow-400 font-bold">+{{ connectedInfo.phone }}</span>
            <span v-if="connectedInfo.name"> · {{ connectedInfo.name }}</span>
          </p>
          <button
            type="button"
            class="px-5 py-2.5 rounded-lg text-sm font-bold text-black"
            style="background: linear-gradient(135deg,#fbbf24,#d97706);"
            @click="finish"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
